import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface ModulePermissions {
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canExport: boolean
  canDelete: boolean
}

export interface UserPermissions {
  project: ModulePermissions
  company: ModulePermissions
  finance: ModulePermissions
  hr: ModulePermissions
  mission_order: ModulePermissions
  support: ModulePermissions
  wiki: ModulePermissions
  dev: ModulePermissions
}

export interface UserRoles {
  is_super_admin: boolean
  simulation_active?: boolean
  project: number
  company: number
  finance: number
  hr: number
  mission_order: number
  support: number
  wiki: number
  dev: number
  permissions?: UserPermissions
  is_pdg?: boolean
  is_rh?: boolean
  can_quote?: boolean
  is_dev?: boolean
  lead_cellule_ids?: number[]
}

export interface WorkplaceAddress {
  value: string
  label: string
  street: string
  postal_code: string
  city: string
}

export interface User {
  id: number
  username: string
  email: string
  cellule_id: number | null
  registration_number: string | null
  flag_password?: boolean
  roles: UserRoles
  permissions?: string[]
  workplace_addresses?: WorkplaceAddress[]
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isAuthenticated = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function login(username: string, password: string) {
    loading.value = true
    error.value = null

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Erreur de connexion')
      }

      user.value = data.data.user
      isAuthenticated.value = true

      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Erreur de connexion'
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    loading.value = true

    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      })
    } catch (e) {
      console.error('Erreur lors de la déconnexion', e)
    } finally {
      user.value = null
      isAuthenticated.value = false
      loading.value = false
    }
  }

  async function checkAuth() {
    loading.value = true

    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()
        user.value = data.data.user
        isAuthenticated.value = true
      } else {
        user.value = null
        isAuthenticated.value = false
      }
    } catch (e) {
      user.value = null
      isAuthenticated.value = false
    } finally {
      loading.value = false
    }
  }

  // Computed properties pour les rôles
  const isSuperAdmin = computed(() => user.value?.roles?.is_super_admin ?? false)
  const isSimulationActive = computed(() => user.value?.roles?.simulation_active ?? false)

  // Niveaux de scope: 0=Aucun, 1=Perso, 2=Cellule, 3=CelluleLead, 4=Global
  const SCOPE_GLOBAL = 4

  // Vérifier si l'utilisateur a le niveau GLOBAL pour un module
  const isGlobalProject = computed(() => (user.value?.roles?.project ?? 0) >= SCOPE_GLOBAL)
  const isGlobalFinance = computed(() => (user.value?.roles?.finance ?? 0) >= SCOPE_GLOBAL)

  // Vérifier si l'utilisateur a au moins le niveau demandé pour un module
  function hasScope(module: keyof Omit<UserRoles, 'is_super_admin' | 'simulation_active' | 'permissions'>, minLevel: number): boolean {
    if (!user.value?.roles) return false
    const level = user.value.roles[module]
    return typeof level === 'number' && level >= minLevel
  }

  // Rafraîchir les données utilisateur (après changement de simulation)
  async function refreshUser() {
    await checkAuth()
  }

  return {
    user,
    isAuthenticated,
    loading,
    error,
    isSuperAdmin,
    isSimulationActive,
    isGlobalProject,
    isGlobalFinance,
    login,
    logout,
    checkAuth,
    refreshUser,
    hasScope,
  }
})
